import React, { useEffect, useState, useRef } from 'react';
import { SaveIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAboutData } from '../../lib/cmsStore';
import { AboutPageData } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { Tabs } from '../../components/ui/Tabs';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function AboutEditor() {
  const {
    data,
    updateData,
    isLoading
  } = useAboutData();
  const [localData, setLocalData] = useState<AboutPageData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && data && !localData) {
      setLocalData(data);
    }
  }, [data, isLoading, localData]);
  if (isLoading || !localData) {
    return <div className="p-8 font-mono text-sm text-muted-foreground" data-id="element-531">
        Loading...
      </div>;
  }
  const handleSave = () => {
    setIsSaving(true);
    updateData(localData);
    setTimeout(() => setIsSaving(false), 500);
  };
  const updateField = (field: keyof AboutPageData, value: any) => {
    setLocalData(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const updateArrayItem = (field: keyof AboutPageData, index: number, key: string, value: any) => {
    setLocalData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] as any[])];
      arr[index] = {
        ...arr[index],
        [key]: value
      };
      return {
        ...prev,
        [field]: arr
      };
    });
  };
  const removeArrayItem = (field: keyof AboutPageData, index: number) => {
    setLocalData(prev => {
      if (!prev) return prev;
      const arr = [...(prev[field] as any[])];
      arr.splice(index, 1);
      return {
        ...prev,
        [field]: arr
      };
    });
  };
  const addArrayItem = (field: keyof AboutPageData, emptyItem: any) => {
    setLocalData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: [...(prev[field] as any[]), emptyItem]
      };
    });
  };
  const tabs = [{
    id: 'general',
    label: 'General',
    content: <div className="space-y-8 max-w-3xl py-6" data-id="element-532">
          <div className="space-y-4" data-id="element-533">
            <h3 className="font-display text-xl font-bold border-b border-border pb-2" data-id="element-534">
              Hero & Intro
            </h3>
            <Textarea label="Hero Title" value={localData.heroTitle} onChange={e => updateField('heroTitle', e.target.value)} rows={3} data-id="element-535" />
            <Textarea label="Subtitle" value={localData.subtitle} onChange={e => updateField('subtitle', e.target.value)} rows={3} data-id="element-536" />
            <Textarea label="Intro Paragraph (Optional)" value={localData.introParagraph} onChange={e => updateField('introParagraph', e.target.value)} rows={3} data-id="element-537" />
          </div>

          <div className="space-y-4" data-id="element-538">
            <h3 className="font-display text-xl font-bold border-b border-border pb-2" data-id="element-539">
              Profile Info
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-540">
              <Input label="Location" value={localData.location} onChange={e => updateField('location', e.target.value)} data-id="element-541" />
              <Select label="Availability Status" value={localData.availabilityStatus} onChange={e => updateField('availabilityStatus', e.target.value as any)} options={[{
            label: 'Available',
            value: 'available'
          }, {
            label: 'Busy',
            value: 'busy'
          }, {
            label: 'Unavailable',
            value: 'unavailable'
          }]} data-id="element-542" />
            </div>
            <Input label="Availability Text" value={localData.availabilityText} onChange={e => updateField('availabilityText', e.target.value)} data-id="element-543" />
            <Input label="Profile Image URL" value={localData.profileImage} onChange={e => updateField('profileImage', e.target.value)} data-id="element-544" />
            {localData.profileImage && <img src={localData.profileImage} alt="Profile" className="w-32 h-32 object-cover border border-border" data-id="element-545" />}
          </div>
        </div>
  }, {
    id: 'skills',
    label: 'Skills & Tools',
    content: <div className="space-y-12 max-w-3xl py-6" data-id="element-546">
          <div className="space-y-4" data-id="element-547">
            <div className="flex items-center justify-between border-b border-border pb-2" data-id="element-548">
              <h3 className="font-display text-xl font-bold" data-id="element-549">Skills</h3>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('skills', {
            name: '',
            category: ''
          })} data-id="element-550">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-551" /> Add Skill
              </Button>
            </div>
            <div className="space-y-3" data-id="element-552">
              {localData.skills.map((skill, i) => <div key={i} className="flex items-center gap-3 bg-card border border-border p-3" data-id="element-553">
                  <Input value={skill.name} onChange={e => updateArrayItem('skills', i, 'name', e.target.value)} placeholder="Skill Name" className="flex-1" data-id="element-554" />
                  <Input value={skill.category} onChange={e => updateArrayItem('skills', i, 'category', e.target.value)} placeholder="Category" className="flex-1" data-id="element-555" />
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('skills', i)} className="text-destructive" data-id="element-556">
                    <Trash2Icon className="w-4 h-4" data-id="element-557" />
                  </Button>
                </div>)}
            </div>
          </div>

          <div className="space-y-4" data-id="element-558">
            <div className="flex items-center justify-between border-b border-border pb-2" data-id="element-559">
              <h3 className="font-display text-xl font-bold" data-id="element-560">Tools</h3>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('tools', {
            name: '',
            url: ''
          })} data-id="element-561">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-562" /> Add Tool
              </Button>
            </div>
            <div className="space-y-3" data-id="element-563">
              {localData.tools.map((tool, i) => <div key={i} className="flex items-center gap-3 bg-card border border-border p-3" data-id="element-564">
                  <Input value={tool.name} onChange={e => updateArrayItem('tools', i, 'name', e.target.value)} placeholder="Tool Name" className="flex-1" data-id="element-565" />
                  <Input value={tool.url} onChange={e => updateArrayItem('tools', i, 'url', e.target.value)} placeholder="URL (Optional)" className="flex-1" data-id="element-566" />
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('tools', i)} className="text-destructive" data-id="element-567">
                    <Trash2Icon className="w-4 h-4" data-id="element-568" />
                  </Button>
                </div>)}
            </div>
          </div>
        </div>
  }, {
    id: 'social',
    label: 'Social & Links',
    content: <div className="space-y-8 max-w-3xl py-6" data-id="element-569">
          <div className="space-y-4" data-id="element-570">
            <div className="flex items-center justify-between border-b border-border pb-2" data-id="element-571">
              <h3 className="font-display text-xl font-bold" data-id="element-572">Social Links</h3>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('socialLinks', {
            platform: '',
            url: '',
            visible: true
          })} data-id="element-573">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-574" /> Add Link
              </Button>
            </div>
            <div className="space-y-3" data-id="element-575">
              {localData.socialLinks.map((link, i) => <div key={i} className="flex items-center gap-3 bg-card border border-border p-3" data-id="element-576">
                  <Input value={link.platform} onChange={e => updateArrayItem('socialLinks', i, 'platform', e.target.value)} placeholder="Platform (e.g. GITHUB)" className="w-1/3" data-id="element-577" />
                  <Input value={link.url} onChange={e => updateArrayItem('socialLinks', i, 'url', e.target.value)} placeholder="URL" className="flex-1" data-id="element-578" />
                  <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-579">
                    <input type="checkbox" checked={link.visible} onChange={e => updateArrayItem('socialLinks', i, 'visible', e.target.checked)} className="accent-primary" data-id="element-580" />
                    Show
                  </label>
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('socialLinks', i)} className="text-destructive ml-2" data-id="element-581">
                    <Trash2Icon className="w-4 h-4" data-id="element-582" />
                  </Button>
                </div>)}
            </div>
          </div>
        </div>
  }];
  return <div className="flex flex-col h-full bg-background" data-id="element-583">
      <header className="flex items-center justify-between px-8 py-4 border-b border-border bg-card sticky top-0 z-10" data-id="element-584">
        <div data-id="element-585">
          <h1 className="font-display text-xl font-bold tracking-tight" data-id="element-586">
            About Page
          </h1>
          <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-587">
            Manage your personal info and skills.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-588">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-589" />
          <Button onClick={handleSave} disabled={isSaving} className="gap-2 min-w-[120px]" data-id="element-590">
            <SaveIcon className="w-4 h-4" data-id="element-591" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-hidden p-8 max-w-7xl mx-auto w-full" data-id="element-592">
        <Tabs tabs={tabs} data-id="element-593" />
      </main>
      <VersionHistoryPanel contentKey="sinka_about" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      setLocalData(data);
      updateData(data);
    }} data-id="element-594" />
    </div>;
}